import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { formatPercent } from 'utils';
import { policyColors } from 'components/Charts/Utils';
import { equityVsFixedIncomeIndex } from 'components/Settings/Palette/utils';
import { useTheme } from '@material-ui/styles';
import { radiusHorizontal } from 'utils/charts';
import { tooltipStylePadded } from 'components/Charts/Tooltips/utils';

const Label = (val: any) => {
  if (val.value === 0) return null;
  const offset = (val.height + val.offset) / 2;
  return (
    <text height={val.height} x={val.x + val.width + 10} y={val.y + offset + 1}>
      <tspan fill={val.fill}>{formatPercent(val.value)}</tspan>
    </text>
  );
};

const CountryBreakdownChart = ({
  data,
  legend = null,
  isForReport = false
}: any) => {
  const theme = useTheme() as any;
  const getTextWidth = (text: string, font: any) => {
    /* eslint-disabled */
    // @ts-ignore
    const canvas = document.createElement('canvas');
    const context: any = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  };

  const maxWidthLabel = getTextWidth(
    [...data].sort((a, b) => b.name.length - a.name.length)[0],
    16
  );

  data = data
    .sort((a: any, b: any) => {
      return b.equity + b.fixed_income - (a.equity + a.fixed_income);
    })
    .filter((item: any) => item.equity !== 0 || item.fixed_income !== 0);

  const finalData =
    data && data.length > 0
      ? data
      : [{ name: 'No Data Available', equity: 0, fixed_income: 0 }];

  const minHeight = Math.max(
    300,
    Math.min(820, isForReport ? data.length * 100 : 820)
  );

  return (
    <ResponsiveContainer width="100%" height="100%" minHeight={minHeight}>
      <BarChart
        data={finalData}
        margin={{ top: 5, right: 60, left: 0, bottom: 5 }}
        layout="vertical"
        barGap={5}
        barCategoryGap={5}
        maxBarSize={60}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          horizontalCoordinatesGenerator={props => {
            const data = [] as Array<any>;
            if (props.yAxis) {
              const yAxis = props.yAxis;
              yAxis.domain.forEach((domain: string, index: number) => {
                data.push(yAxis.bandSize * index + props.offset.top);
              });
            }
            return data;
          }}
        />
        <XAxis type="number" tickFormatter={(val: string) => `${val}%`} />
        <YAxis
          dataKey="name"
          width={maxWidthLabel + 55}
          type="category"
          tickLine={false}
        />
        <Tooltip
          formatter={(val: any) => formatPercent(val)}
          contentStyle={tooltipStylePadded}
        />
        <Legend
          content={legend}
          iconType="circle"
          wrapperStyle={{ fontSize: '16px' }}
        />
        <Bar
          dataKey="equity"
          name="Equity"
          fill={
            theme.palette.baseColors.charts['comparisons'][
              equityVsFixedIncomeIndex('equity')
            ]
          }
          label={<Label fill={policyColors.recommended} />}
          isAnimationActive={false}
          radius={radiusHorizontal}
        ></Bar>
        <Bar
          dataKey="fixed_income"
          name="Fixed income"
          fill={
            theme.palette.baseColors.charts['comparisons'][
              equityVsFixedIncomeIndex('fixed-income')
            ]
          }
          label={<Label fill={policyColors.existing} />}
          isAnimationActive={false}
          radius={radiusHorizontal}
        ></Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default CountryBreakdownChart;
